import React, { useState } from 'react';
import './App.css';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

function App() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loadingSteps, setLoadingSteps] = useState([]);
  
  // Novo estado para preview
  const [pdfAnalysis, setPdfAnalysis] = useState(null);
  const [pageSettings, setPageSettings] = useState([]);
  const [showOnlyLarge, setShowOnlyLarge] = useState(false);
  const advancedOptimizationAvailable = pdfAnalysis?.ghostscriptAvailable !== false;

  // Função para estimar tamanho da página após ajustes
  const estimateAdjustedSize = (page) => {
    if (!page.include) return 0;
    if (!advancedOptimizationAvailable) return parseFloat(page.estimatedSizeKB);
    if (page.keepOriginal) return parseFloat(page.estimatedSizeKB);

    const originalSize = parseFloat(page.estimatedSizeKB);
    
    // Fator de qualidade (linear: 100% = 1x, 50% = 0.5x, 20% = 0.2x)
    const qualityFactor = page.quality / 100;
    
    // Fator de DPI (quadrático, pois afeta área: 150 = 2.25x, 100 = 1x, 50 = 0.25x)
    const dpiFactor = Math.pow(page.dpi / 100, 2);
    
    // Fator de redimensionamento para A4
    let resizeFactor = 1;
    if (page.resizeToA4) {
      const A4_WIDTH = 595;
      const A4_HEIGHT = 842;
      const A4_area = A4_WIDTH * A4_HEIGHT;
      const currentArea = page.width * page.height;
      resizeFactor = A4_area / currentArea;
    }
    
    // Aplicar compressão adicional baseada em fatores combinados
    // Ghostscript comprime mais agressivamente com configurações baixas
    const compressionBonus = qualityFactor < 0.5 ? 0.85 : 1;
    
    const estimatedSize = originalSize * qualityFactor * dpiFactor * resizeFactor * compressionBonus;
    
    return Math.max(10, estimatedSize); // Mínimo 10KB por página
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (selectedFiles.length > 0) {
      const file = selectedFiles[0];
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFiles([]);
        setMessage('');
        setPdfAnalysis(null);
        setError(`O arquivo excede o limite de ${MAX_FILE_SIZE_MB}MB. Selecione um arquivo menor para continuar.`);
        setLoadingSteps([]);
        e.target.value = '';
        return;
      }
    }

    setFiles(selectedFiles);
    setMessage('');
    setError('');
    setPdfAnalysis(null);
    setLoadingSteps([]);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const cleanupTempFile = async (tempFilePath) => {
    if (!tempFilePath) {
      return;
    }

    try {
      await fetch(`${API_BASE}/cleanup-temp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tempFilePath })
      });
    } catch (cleanupError) {
      console.error('Erro ao limpar arquivo temporario:', cleanupError);
    }
  };

  const resetEditorState = () => {
    cleanupTempFile(pdfAnalysis?.tempFilePath);
    setFiles([]);
    setPdfAnalysis(null);
    setPageSettings([]);
    setShowOnlyLarge(false);
    setProgress(0);
    setMessage('');
    setError('');
    setLoadingSteps([]);
  };

  // Nova função para analisar PDF antes de processar
  const analyzeFile = async () => {
    if (files.length === 0) {
      setError('Selecione pelo menos um arquivo');
      return;
    }

    // Verificar se é PDF
    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Para preview, apenas arquivos PDF são suportados');
      return;
    }

    setAnalyzing(true);
    setProgress(0);
    setMessage('');
    setError('');
    setLoadingSteps([
      { text: 'Enviando arquivo para análise...', status: 'current' },
      { text: 'Extraindo metadados do PDF...', status: '' },
      { text: `Analisando ${file.name}...`, status: '' },
      { text: 'Gerando miniaturas das páginas...', status: '' },
      { text: '✅ PDF analisado com sucesso!', status: '' }
    ]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      setProgress(15);
      setMessage('Analisando PDF...');

      const response = await fetch(`${API_BASE}/analyze`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (parseError) {
          // Ignora parse quando o backend nao retorna JSON.
        }

        const backendError = errorData?.error || 'Erro ao analisar arquivo';
        if (backendError.includes('File too large')) {
          throw new Error(`O arquivo excede o limite de ${MAX_FILE_SIZE_MB}MB. Tente um PDF menor.`);
        }
        throw new Error(backendError);
      }

      setLoadingSteps([
        { text: 'Enviando arquivo para análise...', status: 'done' },
        { text: 'Extraindo metadados do PDF...', status: 'current' },
        { text: `Analisando ${file.name}...`, status: '' },
        { text: 'Gerando miniaturas das páginas...', status: '' },
        { text: '✅ PDF analisado com sucesso!', status: '' }
      ]);
      setProgress(40);
      await sleep(800);

      const analysis = await response.json();

      setLoadingSteps([
        { text: 'Enviando arquivo para análise...', status: 'done' },
        { text: 'Extraindo metadados do PDF...', status: 'done' },
        { text: `Analisando ${file.name}...`, status: 'current' },
        { text: 'Gerando miniaturas das páginas...', status: '' },
        { text: '✅ PDF analisado com sucesso!', status: '' }
      ]);
      setProgress(65);
      await sleep(800);

      setLoadingSteps([
        { text: 'Enviando arquivo para análise...', status: 'done' },
        { text: 'Extraindo metadados do PDF...', status: 'done' },
        { text: `Analisando ${file.name}...`, status: 'done' },
        { text: 'Gerando miniaturas das páginas...', status: 'current' },
        { text: '✅ PDF analisado com sucesso!', status: '' }
      ]);
      setProgress(85);
      await sleep(800);
      
      setProgress(100);
      setPdfAnalysis(analysis);
      setPageSettings(analysis.pages);
      setMessage(analysis.warning || '');
      setLoadingSteps([
        { text: 'Enviando arquivo para análise...', status: 'done' },
        { text: 'Extraindo metadados do PDF...', status: 'done' },
        { text: `Analisando ${file.name}...`, status: 'done' },
        { text: 'Gerando miniaturas das páginas...', status: 'done' },
        { text: `✅ ${analysis.totalPages} páginas analisadas com sucesso!`, status: 'done' }
      ]);

    } catch (err) {
      console.error('Erro:', err);
      setError('❌ Erro ao analisar arquivo: ' + err.message);
      setLoadingSteps([]);
    } finally {
      setAnalyzing(false);
      setProgress(0);
    }
  };

  // Processar com configurações customizadas
  const processWithSettings = async () => {
    setUploading(true);
    setProgress(0);
    setMessage('');
    setError('');

    const includedCount = pageSettings.filter((p) => p.include).length;
    const compressionStepLabel = advancedOptimizationAvailable
      ? 'Aplicando compressão Ghostscript...'
      : 'Compressão avançada indisponível sem Ghostscript...';
    const baseSteps = [
      { text: `Preparando ${includedCount} páginas para processamento...`, status: 'current' },
      { text: 'Enviando configurações personalizadas...', status: '' },
      { text: compressionStepLabel, status: '' },
      { text: 'Otimizando imagens e redimensionando páginas...', status: '' },
      { text: 'Mesclando páginas no PDF final...', status: '' },
      { text: 'Preparando download...', status: '' },
      { text: '✅ PDF otimizado baixado com sucesso!', status: '' }
    ];
    setLoadingSteps(baseSteps);

    try {
      setProgress(10);
      setMessage('Preparando processamento...');
      await sleep(800);

      setLoadingSteps([
        { text: `Preparando ${includedCount} páginas para processamento...`, status: 'done' },
        { text: 'Enviando configurações personalizadas...', status: 'current' },
        { text: compressionStepLabel, status: '' },
        { text: 'Otimizando imagens e redimensionando páginas...', status: '' },
        { text: 'Mesclando páginas no PDF final...', status: '' },
        { text: 'Preparando download...', status: '' },
        { text: '✅ PDF otimizado baixado com sucesso!', status: '' }
      ]);
      setProgress(25);
      await sleep(800);

      setLoadingSteps([
        { text: `Preparando ${includedCount} páginas para processamento...`, status: 'done' },
        { text: 'Enviando configurações personalizadas...', status: 'done' },
        { text: compressionStepLabel, status: 'current' },
        { text: 'Otimizando imagens e redimensionando páginas...', status: '' },
        { text: 'Mesclando páginas no PDF final...', status: '' },
        { text: 'Preparando download...', status: '' },
        { text: '✅ PDF otimizado baixado com sucesso!', status: '' }
      ]);
      setProgress(40);

      const response = await fetch(`${API_BASE}/process-with-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tempFilePath: pdfAnalysis.tempFilePath,
          pageSettings: pageSettings
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao processar arquivo');
      }

      await sleep(800);
      setLoadingSteps([
        { text: `Preparando ${includedCount} páginas para processamento...`, status: 'done' },
        { text: 'Enviando configurações personalizadas...', status: 'done' },
        { text: compressionStepLabel, status: 'done' },
        { text: 'Otimizando imagens e redimensionando páginas...', status: 'current' },
        { text: 'Mesclando páginas no PDF final...', status: '' },
        { text: 'Preparando download...', status: '' },
        { text: '✅ PDF otimizado baixado com sucesso!', status: '' }
      ]);
      setProgress(55);
      await sleep(800);
      setLoadingSteps([
        { text: `Preparando ${includedCount} páginas para processamento...`, status: 'done' },
        { text: 'Enviando configurações personalizadas...', status: 'done' },
        { text: compressionStepLabel, status: 'done' },
        { text: 'Otimizando imagens e redimensionando páginas...', status: 'done' },
        { text: 'Mesclando páginas no PDF final...', status: 'current' },
        { text: 'Preparando download...', status: '' },
        { text: '✅ PDF otimizado baixado com sucesso!', status: '' }
      ]);
      setProgress(70);
      await sleep(800);
      setLoadingSteps([
        { text: `Preparando ${includedCount} páginas para processamento...`, status: 'done' },
        { text: 'Enviando configurações personalizadas...', status: 'done' },
        { text: compressionStepLabel, status: 'done' },
        { text: 'Otimizando imagens e redimensionando páginas...', status: 'done' },
        { text: 'Mesclando páginas no PDF final...', status: 'done' },
        { text: 'Preparando download...', status: 'current' },
        { text: '✅ PDF otimizado baixado com sucesso!', status: '' }
      ]);
      setProgress(85);
      setMessage('Gerando PDF otimizado...');

      // Obter o blob do arquivo
      const blob = await response.blob();
      const finalSizeMB = (blob.size / (1024 * 1024)).toFixed(2);
      
      // Criar URL para download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'documento-otimizado.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setProgress(100);
      const successMessage = advancedOptimizationAvailable
        ? `✅ PDF otimizado (${finalSizeMB}MB) baixado com sucesso!`
        : `⚠️ PDF gerado em modo básico (${finalSizeMB}MB). Instale o Ghostscript para aplicar qualidade, DPI e redimensionamento.`;
      setMessage(successMessage);
      setLoadingSteps([
        { text: `Preparando ${includedCount} páginas para processamento...`, status: 'done' },
        { text: 'Enviando configurações personalizadas...', status: 'done' },
        { text: compressionStepLabel, status: 'done' },
        { text: 'Otimizando imagens e redimensionando páginas...', status: 'done' },
        { text: 'Mesclando páginas no PDF final...', status: 'done' },
        { text: 'Preparando download...', status: 'done' },
        { text: successMessage, status: 'done' }
      ]);

    } catch (err) {
      console.error('Erro:', err);
      setError('❌ Erro ao processar arquivo: ' + err.message);
      setLoadingSteps([]);
    } finally {
      setUploading(false);
    }
  };

  // Atualizar configuração de uma página específica
  const updatePageSetting = (pageNumber, field, value) => {
    setPageSettings(prev => 
      prev.map(page => 
        page.pageNumber === pageNumber 
          ? { ...page, [field]: value }
          : page
      )
    );
  };

  // Aplicar configuração para todas as páginas
  const applyToAll = (field, value) => {
    setPageSettings(prev =>
      prev.map(page => ({ ...page, [field]: value }))
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const includedPages = pageSettings.filter(p => p.include).length;
  
  // Calcular tamanhos originais e ajustados
  const totalOriginalKB = pageSettings.reduce((sum, page) => 
    sum + (page.include ? parseFloat(page.estimatedSizeKB) : 0), 0
  );
  const totalAdjustedKB = pageSettings.reduce((sum, page) => 
    sum + estimateAdjustedSize(page), 0
  );
  
  const compressionPercentage = totalOriginalKB > 0 
    ? (((totalOriginalKB - totalAdjustedKB) / totalOriginalKB) * 100).toFixed(1)
    : 0;
  
  // Filtrar páginas para exibição
  const filteredPages = showOnlyLarge 
    ? pageSettings.filter(p => p.isLarge) 
    : pageSettings;

  // Se houver análise, mostrar tela de preview
  if (pdfAnalysis && !uploading) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>📄 Editor de Páginas - {pdfAnalysis.filename}</h1>
          <p className="subtitle">
            {pdfAnalysis.totalPages} páginas • {pdfAnalysis.totalSizeMB}MB • 
            {pdfAnalysis.hasLargePages && <span className="warning"> ⚠️ Contém páginas grandes</span>}
          </p>
          {!advancedOptimizationAvailable && (
            <p className="feature-warning">
              Ghostscript não está instalado. Qualidade, DPI e redimensionamento avançado ficam indisponíveis neste modo.
            </p>
          )}
        </header>

        <main className="App-main preview-mode">
          {/* Estimativa de compressão */}
          <div className="compression-summary">
            <div className="size-info">
              <div className="size-original">
                <span className="label">Tamanho original:</span>
                <span className="value">{(totalOriginalKB / 1024).toFixed(2)} MB</span>
              </div>
              <div className="size-arrow">→</div>
              <div className="size-adjusted">
                <span className="label">Estimado ajustado:</span>
                <span className="value">{(totalAdjustedKB / 1024).toFixed(2)} MB</span>
              </div>
              <div className="size-reduction">
                <span className="reduction-badge">-{compressionPercentage}%</span>
              </div>
            </div>
          </div>

          {/* Controles globais */}
          <div className="global-controls">
            <div className="control-group">
              <label>Aplicar qualidade para todas:</label>
              <input 
                type="range" 
                min="20" 
                max="100" 
                defaultValue="80"
                onChange={(e) => applyToAll('quality', parseInt(e.target.value))}
                disabled={!advancedOptimizationAvailable}
              />
            </div>
            <div className="control-group">
              <label>Aplicar DPI para todas:</label>
              <input 
                type="range" 
                min="50" 
                max="150" 
                defaultValue="100"
                onChange={(e) => applyToAll('dpi', parseInt(e.target.value))}
                disabled={!advancedOptimizationAvailable}
              />
            </div>
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${!showOnlyLarge ? 'active' : ''}`}
                onClick={() => setShowOnlyLarge(false)}
              >
                📄 Todas ({pdfAnalysis.totalPages})
              </button>
              <button 
                className={`filter-btn ${showOnlyLarge ? 'active' : ''}`}
                onClick={() => setShowOnlyLarge(true)}
              >
                ⚠️ Apenas grandes ({pageSettings.filter(p => p.isLarge).length})
              </button>
            </div>
            <div className="page-counter">
              <strong>{includedPages}</strong> de <strong>{pdfAnalysis.totalPages}</strong> páginas selecionadas
            </div>
          </div>

          {/* Grid de páginas */}
          <div className="pages-grid">
            {filteredPages.map((page) => (
              <div 
                key={page.pageNumber} 
                className={`page-card ${page.isLarge ? 'large-page' : ''} ${!page.include ? 'excluded' : ''}`}
              >
                <div className="page-thumbnail">
                  <img src={page.thumbnail} alt={`Página ${page.pageNumber}`} />
                  {page.isLarge && <div className="large-badge">⚠️ GRANDE</div>}
                </div>
                
                <div className="page-info">
                  <h4>Página {page.pageNumber}</h4>
                  <p className="page-dimensions">{page.width}x{page.height}px</p>
                  <div className="page-size-comparison">
                    <span className="original-size">~{page.estimatedSizeKB}KB</span>
                    <span className="arrow">→</span>
                    <span className="adjusted-size">~{estimateAdjustedSize(page).toFixed(2)}KB</span>
                  </div>
                  {page.include && !page.keepOriginal && (
                    <p className="size-reduction-badge">
                      {(((parseFloat(page.estimatedSizeKB) - estimateAdjustedSize(page)) / parseFloat(page.estimatedSizeKB)) * 100).toFixed(0)}% menor
                    </p>
                  )}
                </div>

                <div className="page-controls">
                  <label className="control-item checkbox-item">
                    <input 
                      type="checkbox"
                      checked={page.include}
                      onChange={(e) => updatePageSetting(page.pageNumber, 'include', e.target.checked)}
                    />
                    <span>Incluir página</span>
                  </label>

                  <label className="control-item checkbox-item">
                    <input 
                      type="checkbox"
                      checked={page.resizeToA4}
                      onChange={(e) => updatePageSetting(page.pageNumber, 'resizeToA4', e.target.checked)}
                      disabled={!page.include || page.keepOriginal || !advancedOptimizationAvailable}
                    />
                    <span>📐 Redimensionar para A4 (595x842)</span>
                  </label>

                  <label className="control-item checkbox-item">
                    <input 
                      type="checkbox"
                      checked={page.keepOriginal}
                      onChange={(e) => {
                        updatePageSetting(page.pageNumber, 'keepOriginal', e.target.checked);
                        // Se marcar "manter original", desabilitar redimensionamento
                        if (e.target.checked) {
                          updatePageSetting(page.pageNumber, 'resizeToA4', false);
                        }
                      }}
                      disabled={!page.include}
                    />
                    <span>🔒 Manter original (sem compressão)</span>
                  </label>

                  <label className="control-item">
                    <span>Qualidade: {page.quality}%</span>
                    <input 
                      type="range"
                      min="20"
                      max="100"
                      value={page.quality}
                      onChange={(e) => updatePageSetting(page.pageNumber, 'quality', parseInt(e.target.value))}
                      disabled={!page.include || page.keepOriginal || !advancedOptimizationAvailable}
                    />
                  </label>

                  <label className="control-item">
                    <span>DPI: {page.dpi}</span>
                    <input 
                      type="range"
                      min="50"
                      max="150"
                      value={page.dpi}
                      onChange={(e) => updatePageSetting(page.pageNumber, 'dpi', parseInt(e.target.value))}
                      disabled={!page.include || page.keepOriginal || !advancedOptimizationAvailable}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* Botões de ação */}
          <div className="action-buttons">
            <button 
              className="secondary-button"
              onClick={resetEditorState}
            >
              ← Voltar
            </button>
            
            <button
              className="upload-button"
              onClick={processWithSettings}
              disabled={includedPages === 0}
            >
              🚀 Processar e Baixar PDF ({includedPages} páginas)
            </button>
          </div>

          {message && !error && (
            <div className="message success">{message}</div>
          )}

          {error && (
            <div className="message error">{error}</div>
          )}
        </main>
      </div>
    );
  }

  // Tela de upload (original)
  return (
    <div className="App">
      <header className="App-header">
        <h1>📄 Otimizador de Documentos</h1>
        <p className="subtitle">Converta e otimize seus documentos em PDF</p>
      </header>

      <main className="App-main">
        <div className="upload-container">
          <div className="upload-area">
            <input
              type="file"
              id="file-input"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp"
              onChange={handleFileChange}
              disabled={uploading || analyzing}
            />
            <label htmlFor="file-input" className="file-label">
              <div className="upload-icon">📎</div>
              <div className="upload-text">
                {files.length === 0 ? (
                  <>
                    <strong>Clique para selecionar arquivo PDF</strong>
                    <span>ou arraste e solte aqui</span>
                  </>
                ) : (
                  <>
                    <strong>{files[0].name}</strong>
                    <span>{formatFileSize(totalSize)}</span>
                  </>
                )}
              </div>
            </label>
            <div className="file-size-limit">
              📦 Tamanho máximo: 50MB por arquivo
            </div>
          </div>

          {files.length > 0 && (
            <div className="file-list">
              <h3>Arquivo selecionado:</h3>
              <ul>
                <li>
                  <span className="file-name">{files[0].name}</span>
                  <span className="file-size">{formatFileSize(files[0].size)}</span>
                </li>
              </ul>
            </div>
          )}

          <button
            className="upload-button"
            onClick={analyzeFile}
            disabled={uploading || analyzing || files.length === 0}
          >
            {analyzing ? '⏳ Analisando...' : '🔍 Analisar e Editar Páginas'}
          </button>

          {(uploading || analyzing) && (
            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <div className="progress-text">{progress}%</div>
              {loadingSteps.length > 0 && (
                <div className="loading-steps">
                  {loadingSteps.map((step, index) => (
                    <div key={`${step.text}-${index}`} className={`loading-step ${step.status || ''}`}>
                      {step.status === 'done' ? '✓ ' : step.status === 'current' ? '→ ' : '• '}
                      {step.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {message && !error && (
            <div className="message success">{message}</div>
          )}

          {error && (
            <div className="message error">{error}</div>
          )}
        </div>

        <div className="info-section">
          <h3>ℹ️ Como funciona</h3>
          <ul>
            <li>📤 Faça upload do seu PDF</li>
            <li>👁️ Visualize todas as páginas com miniaturas</li>
            <li>⚙️ Ajuste qualidade e DPI por página</li>
            <li>🗑️ Remova páginas desnecessárias</li>
            <li>⚠️ Páginas grandes são destacadas automaticamente</li>
          </ul>
        </div>
      </main>

      <footer className="App-footer">
        <p>Sistema Interno de Otimização de Documentos v2.0</p>
      </footer>
    </div>
  );
}

export default App;
